import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Breadcrumb from '../../components/Breadcrumb';
import { useToast } from '../../components/Toast';
import { fetchTaskOverview, type TaskOverviewResp, type WorkerTaskItem, type PollTaskGroup, type ProviderConcurrencyItem } from '../../api/adminTasks';
import './TaskQueueDashboard.css';

type TabType = 'worker' | 'poll';

const ELEMENT_TYPE_LABEL: Record<string, string> = {
  character: '角色',
  scene: '场景',
  prop: '道具',
};

const CATEGORY_LABEL: Record<number, string> = {
  1: '角色',
  2: '场景',
  3: '道具',
};

// Coerce an untrusted numeric field to a non-negative integer, defaulting to 0
// for missing / NaN / negative values so a bad payload never yields a negative
// or fractional count in the progress cell.
function toCount(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
}

function formatElapsed(createdAt: string | undefined): string {
  if (!createdAt) return '—';
  const created = new Date(createdAt);
  if (isNaN(created.getTime())) return '—';
  const diff = Math.floor((Date.now() - created.getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m${diff % 60}s`;
  return `${Math.floor(diff / 3600)}h${Math.floor((diff % 3600) / 60)}m`;
}

function formatDateTime(value: string | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString('zh-CN', { hour12: false });
}

function StatusBadge({ status }: { status: string }) {
  const isPending = ['pending', 'queued'].includes(status);
  const label = isPending ? '待消费' : '处理中';
  return <span className={`task-queue-status ${isPending ? 'is-pending' : 'is-running'}`}>{label}</span>;
}

type TaskQueueToast = ReturnType<typeof useToast>;

function TransferStatus({ task, toast }: { task: Record<string, unknown>; toast: TaskQueueToast }) {
  const status = String(task.transfer_status || 'polling_provider');
  const labels: Record<string, string> = {
    polling_provider: '平台生成',
    waiting_fetch_slot: '等待转存',
    fetching_to_tos: 'TOS 转存',
  };
  const fetchTaskID = String(task.tos_fetch_task_id || '');
  const objectKey = String(task.tos_fetch_object_key || '');
  const originalURL = String(task.original_video_url || '');
  const copyOriginalURL = async () => {
    if (!originalURL) return;
    try {
      await navigator.clipboard.writeText(originalURL);
      toast.success('已复制原始视频地址');
    } catch {
      toast.error('复制失败');
    }
  };
  return (
    <div>
      <span className={`task-queue-status ${status === 'fetching_to_tos' ? 'is-running' : 'is-pending'}`}>
        {labels[status] || status}
      </span>
      {fetchTaskID && <div className="task-queue-cell-meta"><code>{fetchTaskID}</code></div>}
      {objectKey && <div className="task-queue-cell-meta">{objectKey}</div>}
      {originalURL && status !== 'polling_provider' && (
        <div className="task-queue-cell-meta">
          <button className="task-queue-inline-button" type="button" onClick={copyOriginalURL}>
            复制原始视频地址
          </button>
        </div>
      )}
    </div>
  );
}

function ProviderConcurrencyPanel({ items }: { items: ProviderConcurrencyItem[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="task-queue-panel" style={{ marginBottom: '1rem' }}>
      <div className="task-queue-table-wrap">
        <table className="task-queue-table">
          <thead>
            <tr><th>供应商</th><th>进行中 / 上限</th></tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const unlimited = !it.limit || it.limit <= 0;
              const atCap = !unlimited && it.in_flight >= it.limit;
              const near = !unlimited && !atCap && it.in_flight >= Math.ceil(it.limit * 0.8);
              const cls = atCap ? 'is-running' : near ? 'is-pending' : '';
              return (
                <tr key={it.key}>
                  <td>{it.name || it.key}</td>
                  <td><span className={`task-queue-status ${cls}`}>{toCount(it.in_flight)} / {unlimited ? '不限' : it.limit}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function TaskQueueDashboard() {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const [data, setData] = useState<TaskOverviewResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('worker');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchTaskOverview();
      setData(res);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '加载失败';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadData(); }, [loadData]);

  const workerTasks = data?.worker_tasks ?? { pending_count: 0, running_count: 0, tasks: [] };
  const pollTasks = data?.poll_tasks ?? {};
  const summary = data?.summary ?? { total_pending: 0, total_running: 0 };

  const pollTotal = Object.values(pollTasks).reduce(
    (acc, g) => acc + g.pending_count + g.running_count, 0,
  );
  const workerTotal = workerTasks.pending_count + workerTasks.running_count;

  return (
    <div className="task-queue-shell">
      <Breadcrumb items={[
        { label: t('projects.breadcrumbHome'), to: '/projects' },
        { label: t('taskQueue.title', { defaultValue: '任务队列监控' }) },
      ]} />

      <div className="task-queue-header">
        <h1 className="page-title task-queue-title">
          {t('taskQueue.title', { defaultValue: '任务队列监控' })}
        </h1>
        <button
          className="btn btn-secondary"
          onClick={loadData}
          disabled={loading}
        >
          {loading ? t('taskQueue.loading', { defaultValue: '加载中...' }) : t('taskQueue.refresh', { defaultValue: '刷新' })}
        </button>
      </div>

      <div className="task-queue-summary-grid">
        <div className="task-queue-summary-card is-pending">
          <div className="task-queue-summary-label">{t('taskQueue.pending', { defaultValue: '待消费' })}</div>
          <div className="task-queue-summary-value">{summary.total_pending}</div>
          <div className="task-queue-summary-detail">
              Worker {workerTasks.pending_count} · {t('taskQueue.poll', { defaultValue: '轮询' })} {summary.total_pending - workerTasks.pending_count}
          </div>
        </div>
        <div className="task-queue-summary-card is-running">
          <div className="task-queue-summary-label">{t('taskQueue.running', { defaultValue: '处理中' })}</div>
          <div className="task-queue-summary-value">{summary.total_running}</div>
          <div className="task-queue-summary-detail">
              Worker {workerTasks.running_count} · {t('taskQueue.poll', { defaultValue: '轮询' })} {summary.total_running - workerTasks.running_count}
          </div>
        </div>
      </div>

      <ProviderConcurrencyPanel items={data?.provider_concurrency || []} />

      <div className="task-queue-tabs" role="tablist">
        <button
          onClick={() => setActiveTab('worker')}
          className={`task-queue-tab ${activeTab === 'worker' ? 'is-active' : ''}`}
          role="tab"
          aria-selected={activeTab === 'worker'}
        >
          Worker ({workerTotal})
        </button>
        <button
          onClick={() => setActiveTab('poll')}
          className={`task-queue-tab ${activeTab === 'poll' ? 'is-active' : ''}`}
          role="tab"
          aria-selected={activeTab === 'poll'}
        >
          {t('taskQueue.poll', { defaultValue: '轮询' })} ({pollTotal})
        </button>
      </div>

      {activeTab === 'worker' && <WorkerTasksTab tasks={workerTasks.tasks} />}
      {activeTab === 'poll' && <PollTasksTab groups={pollTasks} toast={toast} />}
    </div>
  );
}

function WorkerTasksTab({ tasks }: { tasks: WorkerTaskItem[] }) {
  if (tasks.length === 0) {
    return <div className="task-queue-panel task-queue-empty">暂无任务</div>;
  }
  return (
    <div className="task-queue-panel">
      <div className="task-queue-table-wrap">
      <table className="task-queue-table">
        <thead>
          <tr>
            <th>任务类型</th>
            <th>项目</th>
            <th>状态</th>
            <th>等待时间</th>
            <th>消费者</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => (
            <tr key={task.message_id}>
              <td><code>{task.task_type}</code></td>
              <td>{task.project_name || task.project_id || '—'}</td>
              <td><StatusBadge status={task.status} /></td>
              <td>{task.status === 'running' ? `${Math.floor(task.idle_time_ms / 1000)}s` : formatElapsed(task.created_at)}</td>
              <td>{task.consumer || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

const POLL_GROUP_ORDER = ['video', 'ai_clip', 'image', 'ref_translate', 'asset_review', 'resource_review', 'ext_analysis', 'subtitle_erase', 'voice', 'ref_search'];
const POLL_GROUP_LABELS: Record<string, string> = {
  video: '视频生成',
  ai_clip: 'AI剪辑',
  image: '图片生成',
  ref_translate: '参考翻译',
  asset_review: '人物审核',
  resource_review: '资源审核',
  ext_analysis: '外部分析',
  subtitle_erase: '字幕擦除',
  voice: '语音提取/换声',
  ref_search: '参考图搜索',
};

function PollTasksTab({ groups, toast }: { groups: Record<string, PollTaskGroup>; toast: TaskQueueToast }) {
  const hasAny = Object.values(groups).some((g) => g.tasks.length > 0);
  if (!hasAny) {
    return <div className="task-queue-panel task-queue-empty">暂无任务</div>;
  }

  return (
    <div className="task-queue-panel task-queue-poll-panel">
      {POLL_GROUP_ORDER.map((key) => {
        const group = groups[key];
        if (!group || group.tasks.length === 0) return null;
        return <PollGroupSection key={key} groupKey={key} group={group} toast={toast} />;
      })}
    </div>
  );
}

function PollGroupSection({ groupKey, group, toast }: { groupKey: string; group: PollTaskGroup; toast: TaskQueueToast }) {
  return (
    <div className="task-queue-poll-group">
      <div className="task-queue-group-header">
        <span className="task-queue-group-title">{POLL_GROUP_LABELS[groupKey] || groupKey}</span>
        {group.pending_count > 0 && (
          <span className="task-queue-count-badge is-pending">
            {group.pending_count} 待处理
          </span>
        )}
        {group.running_count > 0 && (
          <span className="task-queue-count-badge is-running">
            {group.running_count} 处理中
          </span>
        )}
      </div>

      {groupKey === 'video' && <VideoTaskTable tasks={group.tasks} toast={toast} />}
      {groupKey === 'ai_clip' && <AIClipTaskTable tasks={group.tasks} />}
      {groupKey === 'image' && <ImageTaskTable tasks={group.tasks} />}
      {groupKey === 'ref_translate' && <RefTranslateTaskTable tasks={group.tasks} />}
      {groupKey === 'ext_analysis' && <ExtAnalysisTaskTable tasks={group.tasks} />}
      {groupKey === 'subtitle_erase' && <SubtitleEraseTaskTable tasks={group.tasks} />}
      {groupKey === 'voice' && <VoiceTaskTable tasks={group.tasks} />}
      {groupKey === 'ref_search' && <RefSearchTaskTable tasks={group.tasks} />}
      {groupKey === 'asset_review' && <AssetReviewTaskTable tasks={group.tasks} />}
      {groupKey === 'resource_review' && <ResourceReviewTaskTable tasks={group.tasks} />}
    </div>
  );
}

function VideoTaskTable({ tasks, toast }: { tasks: Record<string, unknown>[]; toast: TaskQueueToast }) {
  return (
    <div className="task-queue-table-wrap">
      <table className="task-queue-table">
        <thead><tr><th>项目 / 位置</th><th>状态</th><th>转存</th><th>平台</th><th>模型</th><th>视频输入</th><th>耗时</th></tr></thead>
        <tbody>
          {tasks.map((t) => (
            <tr key={String(t.id)}>
              <td>
                <div>{String(t.project_name || '—')}</div>
                <div className="task-queue-cell-meta">{String(t.episode_title || '')} · Shot {String(t.shot_index ?? '')} · v{String(t.version ?? '')}</div>
              </td>
              <td><StatusBadge status={String(t.status)} /></td>
              <td><TransferStatus task={t} toast={toast} /></td>
              <td>{String(t.platform || '—')}</td>
              <td>{String(t.model || '—')}</td>
              <td>{t.has_video_input ? '有' : '无'}</td>
              <td>{formatElapsed(String(t.created_at))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const AI_CLIP_STATUS_LABELS: Record<string, string> = {
  submitting: '提交中',
  queued: '排队中',
  running: '剪辑中',
  transferring: '结果转存中',
};

function AIClipTaskTable({ tasks }: { tasks: Record<string, unknown>[] }) {
  return (
    <div className="task-queue-table-wrap">
      <table className="task-queue-table">
        <thead><tr><th>项目 / 剧集</th><th>状态</th><th>阶段</th><th>远端任务</th><th>转存轮次</th><th>成片时长</th><th>耗时</th></tr></thead>
        <tbody>
          {tasks.map((t) => {
            const status = String(t.status || '');
            const isPending = status === 'submitting' || status === 'queued';
            return (
              <tr key={String(t.id)}>
                <td>
                  <div>{String(t.project_name || '—')}</div>
                  <div className="task-queue-cell-meta">{String(t.episode_title || t.episode_id || '')}</div>
                </td>
                <td>
                  <span className={`task-queue-status ${isPending ? 'is-pending' : 'is-running'}`}>
                    {AI_CLIP_STATUS_LABELS[status] || status}
                  </span>
                </td>
                <td>{String(t.stage || '—')}</td>
                <td>{t.remote_job_present ? '已创建' : '未创建'}</td>
                <td>{String(t.transfer_round ?? '—')}</td>
                <td>{t.video_duration ? `${t.video_duration}s` : '—'}</td>
                <td>{formatElapsed(String(t.created_at))}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RefTranslateTaskTable({ tasks }: { tasks: Record<string, unknown>[] }) {
  return (
    <div className="task-queue-table-wrap">
      <table className="task-queue-table">
        <thead><tr><th>项目 / 剧集</th><th>语言</th><th>状态</th><th>更新时间</th><th>耗时</th></tr></thead>
        <tbody>
          {tasks.map((t) => (
            <tr key={String(t.id)}>
              <td>
                <div>{String(t.project_name || '—')}</div>
                <div className="task-queue-cell-meta">{String(t.episode_title || t.episode_id || '')}</div>
              </td>
              <td>{String(t.language || '—')}</td>
              <td><StatusBadge status={String(t.status)} /></td>
              <td>{formatDateTime(String(t.updated_at || t.created_at || ''))}</td>
              <td>{formatElapsed(String(t.created_at || t.updated_at || ''))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ImageTaskTable({ tasks }: { tasks: Record<string, unknown>[] }) {
  return (
    <div className="task-queue-table-wrap">
      <table className="task-queue-table">
        <thead><tr><th>项目 / 资产</th><th>状态</th><th>类型</th><th>平台</th><th>模型</th><th>标记</th><th>耗时</th></tr></thead>
        <tbody>
          {tasks.map((t) => (
            <tr key={String(t.id)}>
              <td>
                <div>{String(t.project_name || '—')}</div>
                <div className="task-queue-cell-meta">{String(t.element_name || '')}</div>
              </td>
              <td><StatusBadge status={String(t.status)} /></td>
              <td>{ELEMENT_TYPE_LABEL[String(t.element_type)] || String(t.element_type)}</td>
              <td>{String(t.platform || '—')}</td>
              <td>{String(t.model || '—')}</td>
              <td>{t.is_base_image ? '基础图' : t.is_style_test ? '风格测试' : '—'}</td>
              <td>{formatElapsed(String(t.created_at))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ExtAnalysisTaskTable({ tasks }: { tasks: Record<string, unknown>[] }) {
  return (
    <div className="task-queue-table-wrap">
      <table className="task-queue-table">
        <thead><tr><th>项目</th><th>状态</th><th>阶段进度</th><th>重试</th><th>耗时</th></tr></thead>
        <tbody>
          {tasks.map((t) => {
            let stagesCompleted: string[] = [];
            let stagesTotal: string[] = [];
            try { stagesCompleted = JSON.parse(String(t.stages_completed || '[]')); } catch { /* empty */ }
            try { stagesTotal = JSON.parse(String(t.stages_total || '[]')); } catch { /* empty */ }
            // Prefer the partner's fine-grained progress counter (sub-tasks within
            // a stage, e.g. 45/133) and fall back to stage cardinality when it is
            // absent (progressTotal === 0). Shown as numerator/denominator.
            const progressCompleted = toCount(t.progress_completed);
            const progressTotal = toCount(t.progress_total);
            const useNumeric = progressTotal > 0;
            const completed = useNumeric ? progressCompleted : stagesCompleted.length;
            const total = useNumeric ? progressTotal : stagesTotal.length;
            const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
            return (
              <tr key={String(t.id)}>
                <td>{String(t.project_name || '—')}</td>
                <td><StatusBadge status={String(t.status)} /></td>
                <td>
                  <div className="task-queue-progress-cell">
                    <div className="task-queue-progress-track">
                      <div className="task-queue-progress-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="task-queue-cell-meta">{completed}/{total}</span>
                  </div>
                </td>
                <td>{String(t.retry_count ?? 0)}</td>
                <td>{formatElapsed(String(t.created_at))}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SubtitleEraseTaskTable({ tasks }: { tasks: Record<string, unknown>[] }) {
  return (
    <div className="task-queue-table-wrap">
      <table className="task-queue-table">
        <thead><tr><th>项目 / 位置</th><th>状态</th><th>耗时</th></tr></thead>
        <tbody>
          {tasks.map((t) => (
            <tr key={String(t.id)}>
              <td>
                <div>{String(t.project_name || '—')}</div>
                <div className="task-queue-cell-meta">{String(t.episode_title || '')} · Shot {String(t.shot_index ?? '')} · v{String(t.version ?? '')}</div>
              </td>
              <td><StatusBadge status={String(t.status)} /></td>
              <td>{formatElapsed(String(t.created_at))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VoiceTaskTable({ tasks }: { tasks: Record<string, unknown>[] }) {
  return (
    <div className="task-queue-table-wrap">
      <table className="task-queue-table">
        <thead><tr><th>项目</th><th>类型</th><th>状态</th><th>耗时</th></tr></thead>
        <tbody>
          {tasks.map((t) => (
            <tr key={String(t.id)}>
              <td>{String(t.project_name || '—')}</td>
              <td>{t.task_kind === 'extraction' ? '提取' : '换声'}</td>
              <td><StatusBadge status={String(t.status)} /></td>
              <td>{formatElapsed(String(t.created_at))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RefSearchTaskTable({ tasks }: { tasks: Record<string, unknown>[] }) {
  return (
    <div className="task-queue-table-wrap">
      <table className="task-queue-table">
        <thead><tr><th>项目 / 资产</th><th>类型</th><th>关键词</th><th>状态</th></tr></thead>
        <tbody>
          {tasks.map((t) => (
            <tr key={String(t.id)}>
              <td>
                <div>{String(t.project_name || '—')}</div>
                <div className="task-queue-cell-meta">{String(t.asset_name || '')}{t.asset_state ? `（${String(t.asset_state)}）` : ''}</div>
              </td>
              <td>{CATEGORY_LABEL[Number(t.category)] || '—'}</td>
              <td className="task-queue-keywords">{String(t.keywords || '—')}</td>
              <td><StatusBadge status={String(t.status)} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AssetReviewTaskTable({ tasks }: { tasks: Record<string, unknown>[] }) {
  return (
    <div className="task-queue-table-wrap">
      <table className="task-queue-table">
        <thead><tr><th>项目 / 人物</th><th>状态</th><th>平台</th><th>资产ID</th><th>耗时</th></tr></thead>
        <tbody>
          {tasks.map((t) => (
            <tr key={String(t.id)}>
              <td>
                <div>{String(t.project_name || '—')}</div>
                <div className="task-queue-cell-meta">{String(t.character_name || '')}{t.character_state ? `（${String(t.character_state)}）` : ''}</div>
              </td>
              <td><StatusBadge status={String(t.status)} /></td>
              <td>{String(t.platform || '—')}</td>
              <td className="task-queue-keywords">{String(t.asset_id || '—')}</td>
              <td>{formatElapsed(String(t.created_at))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResourceReviewTaskTable({ tasks }: { tasks: Record<string, unknown>[] }) {
  return (
    <div className="task-queue-table-wrap">
      <table className="task-queue-table">
        <thead><tr><th>项目 / 资源</th><th>状态</th><th>类型</th><th>平台</th><th>耗时</th></tr></thead>
        <tbody>
          {tasks.map((t) => (
            <tr key={String(t.id)}>
              <td>
                <div>{String(t.project_name || '—')}</div>
                <div className="task-queue-cell-meta">{String(t.file_name || t.name || '')}</div>
              </td>
              <td><StatusBadge status={String(t.status)} /></td>
              <td>{String(t.resource_type || '—')}</td>
              <td>{String(t.platform || '—')}</td>
              <td>{formatElapsed(String(t.created_at))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
